import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Lock } from "lucide-react";
import { api, setAdminToken, getAdminToken } from "@/lib/api";
import { useEffect } from "react";

export default function AdminLoginPage() {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (getAdminToken()) navigate("/admin/inbox", { replace: true });
  }, [navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!password) {
      toast.error("Please enter the admin password.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/admin/login", { password });
      setAdminToken(data.token);
      toast.success("Welcome back, Wanakee.");
      navigate("/admin/inbox", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="admin-login-page"
      className="min-h-screen flex items-center justify-center wt-bg"
      style={{ backgroundColor: "var(--wt-bg)" }}
    >
      <div className="w-full max-w-md px-6 py-12">
        <Link to="/" data-testid="admin-brand-link" className="flex items-center gap-3">
          <span
            aria-hidden="true"
            className="inline-flex h-9 w-9 items-center justify-center rounded-md font-serif"
            style={{
              backgroundColor: "var(--wt-primary)",
              color: "var(--wt-bg)",
              boxShadow: "inset 0 -2px 0 var(--wt-gold)",
            }}
          >
            W
          </span>
          <span className="flex flex-col leading-tight">
            <span className="font-serif text-base font-semibold wt-primary">Wanakee Trask</span>
            <span className="text-[10px] uppercase tracking-[0.18em] wt-text-muted">
              Admin Console
            </span>
          </span>
        </Link>

        <form
          data-testid="admin-login-form"
          onSubmit={onSubmit}
          className="mt-10 wt-card p-7"
        >
          <div className="flex items-center gap-2">
            <Lock size={16} style={{ color: "var(--wt-primary)" }} />
            <h1 className="wt-h3">Sign in</h1>
          </div>
          <p className="mt-2 text-sm wt-text-muted">
            Enter the admin password to review inquiries and update their status.
          </p>
          <div className="mt-6 flex flex-col gap-2">
            <label htmlFor="password" className="wt-label">
              <span>Admin password</span>
              <span className="wt-label-tag required">Required</span>
            </label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              className="wt-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-login-password"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit-button"
            className="wt-btn-primary mt-6 w-full h-11"
          >
            {loading && <Loader2 size={16} className="animate-spin" />}
            {loading ? "Signing in…" : "Sign in"}
          </button>
          <div className="mt-6 wt-hairline" />
          <p className="mt-4 text-xs wt-text-muted">
            Tip: the password is set on the backend via the <code>ADMIN_PASSWORD</code>{" "}
            environment variable.
          </p>
        </form>
        <Link
          to="/"
          data-testid="admin-back-home"
          className="mt-6 inline-block text-sm wt-link"
        >
          ← Back to website
        </Link>
      </div>
    </div>
  );
}
